import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Building2, FolderOpen, CheckSquare, MessageSquare, UserCheck, X, Settings, Calendar, Timer, Play, Square, Send, Archive, Trash2, ArchiveRestore } from 'lucide-react';
import api from '../utils/api';
import RecurringTaskModal from './RecurringTaskModal';
import InternalMessaging from './InternalMessaging';
import PersonalTaskModal from './PersonalTaskModal';
import PersonalTasksList from './PersonalTasksList';
import PersonalTasksKanban from './PersonalTasksKanban';
import PersonalTasksSettings from './PersonalTasksSettings';
import { RecurringTemplatesList } from './RecurringTemplates';
import RecurringTaskTemplates from './RecurringTaskTemplates';

const OrganizationDashboard = ({ organizationName, view, user, onProjectClick, onNewProject, refreshTrigger }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [projectsTab, setProjectsTab] = useState('projects'); // 'projects' or 'recurring'

  // Form states - simplified
  const [showNewPersonalTaskForm, setShowNewPersonalTaskForm] = useState(false);
  const [selectedPersonalTask, setSelectedPersonalTask] = useState(null);
  const [personalTasksRefreshTrigger, setPersonalTasksRefreshTrigger] = useState(0);
  const [showPersonalTasksSettings, setShowPersonalTasksSettings] = useState(false);

  // Recurring task states
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringRule, setRecurringRule] = useState('');
  const [recurringPreview, setRecurringPreview] = useState('');

  // Team members mock data
  const [teamMembers] = useState([
    { id: 'user1', name: 'John Doe', email: 'john@company.com' },
    { id: 'user2', name: 'Jane Smith', email: 'jane@company.com' },
    { id: 'user3', name: 'Mike Johnson', email: 'mike@company.com' }
  ]);

  // Fetch real projects data
  const [projects, setProjects] = useState([]);
  
  useEffect(() => {
    if (view === 'projects') {
      fetchCompanyProjects();
    }
  }, [view, showArchived, refreshTrigger]);
  
  const fetchCompanyProjects = async () => {
    try {
      const response = await api.get('/projects', {
        params: { show_archived: showArchived }
      });
      // Filter to only company projects (no client_id)
      const companyProjects = response.data.filter(p => !p.client_id);
      setProjects(companyProjects);
      console.log('📦 Loaded company projects:', companyProjects.length);
    } catch (error) {
      console.error('Failed to fetch company projects:', error);
    }
  };

  const getViewData = (viewType) => {
    switch (viewType) {
      case 'projects':
        return projects; // Use real projects data instead of mock
      case 'messaging':
        return [
          { id: '1', subject: 'Q4 Planning Meeting', from: 'CEO', timestamp: '2 hours ago', unread: true },
          { id: '2', subject: 'New Employee Introduction', from: 'HR', timestamp: '1 day ago', unread: false }
        ];
      case 'personal':
        return [
          { id: '1', name: 'Review project proposal', priority: 'High', due_date: '2024-01-15' },
          { id: '2', name: 'Complete training modules', priority: 'Normal', due_date: '2024-01-20' },
          { id: '3', name: 'Update personal profile', priority: 'Low', due_date: '2024-01-25' }
        ];
      default:
        return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    if (view === 'projects') {
      // For projects view, wait for fetchCompanyProjects to complete
      // Data will be set when projects state updates
    } else {
      const viewData = getViewData(view);
      setData(viewData);
    }
    setLoading(false);
  }, [view]);
  
  // Update data when projects are loaded
  useEffect(() => {
    if (view === 'projects') {
      setData(projects);
    }
  }, [projects, view]);

  // Project click handler
  const handleProjectClick = (project) => {
    if (onProjectClick) {
      onProjectClick(project);
    }
  };

  // Archive project handler
  const handleArchiveProject = async (e, projectId, currentlyArchived) => {
    e.stopPropagation(); // Prevent project click
    
    const action = currentlyArchived ? 'unarchive' : 'archive';
    if (!window.confirm(`Are you sure you want to ${action} this project?`)) {
      return;
    }
    
    try {
      await api.put(`/projects/${projectId}/archive`, { archived: !currentlyArchived });
      
      // Refresh projects list
      fetchCompanyProjects();
    } catch (error) {
      console.error(`Failed to ${action} project:`, error);
      alert(`Failed to ${action} project`);
    }
  };

  // Delete project handler
  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation(); // Prevent project click
    
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/projects/${projectId}`);
      
      // Refresh projects list
      fetchCompanyProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // Timer states
  const [currentTimer, setCurrentTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);

  // Fetch current timer status
  useEffect(() => {
    const fetchTimerStatus = async () => {
      try {
        const response = await api.get('/timer/status');
        if (response.data) {
          setCurrentTimer(response.data);
        }
      } catch (error) {
        console.log('No active timer or error fetching timer status');
      }
    };
    
    if (user) {
      fetchTimerStatus();
    }
  }, [user]);

  // Update timer duration every second
  useEffect(() => {
    let interval;
    if (currentTimer?.is_running) {
      interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(currentTimer.start_time);
        const diffMinutes = Math.floor((now - startTime) / (1000 * 60));
        setTimerDuration(diffMinutes);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentTimer]);

  // Personal task handlers
  const handlePersonalTaskTimer = async (taskId) => {
    try {
      if (currentTimer?.is_running) {
        // Stop current timer
        await api.post('/timer', {
          action: 'stop',
          task_id: currentTimer.task_id
        });
        setCurrentTimer(null);
        setTimerDuration(0);
      }
      
      // Start new timer
      const response = await api.post('/timer', {
        action: 'start',
        task_id: taskId
      });
      
      setCurrentTimer(response.data);
    } catch (error) {
      console.error('Timer error:', error);
      alert('Error starting timer. Please try again.');
    }
  };

  const handleEditPersonalTask = (task) => {
    setSelectedPersonalTask(task);
    setShowNewPersonalTaskForm(true);
  };

  const handlePersonalTaskCreated = (newTask) => {
    setPersonalTasksRefreshTrigger(prev => prev + 1);
  };

  const handlePersonalTaskUpdated = (updatedTask) => {
    setPersonalTasksRefreshTrigger(prev => prev + 1);
  };

  const handleDeletePersonalTask = (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      console.log('Deleting task:', taskId);
    }
  };

  // Utility functions
  const getViewTitle = () => {
    switch (view) {
      case 'projects':
        return 'Company Projects';
      case 'messaging':
        return 'Internal Messaging';
      case 'personal':
        return `Hello, ${user?.name || user?.first_name || 'there'}! 👋`;
      case 'recurring':
        return 'Recurring Templates';
      default:
        return 'Organization Dashboard';
    }
  };

  const getViewIcon = () => {
    switch (view) {
      case 'projects':
        return <FolderOpen className="w-6 h-6" />;
      case 'messaging':
        return <MessageSquare className="w-6 h-6" />;
      case 'personal':
        return <UserCheck className="w-6 h-6" />;
      case 'recurring':
        return <span className="text-2xl">🔄</span>;
      default:
        return <Building2 className="w-6 h-6" />;
    }
  };

  const getAddButtonText = () => {
    switch (view) {
      case 'projects':
        return 'New Project';
      case 'messaging':
        return 'New Message';
      case 'personal':
        return 'New Personal Task';
      default:
        return 'Add Item';
    }
  };

  const handleAdd = () => {
    switch (view) {
      case 'projects':
        // Call the parent's onNewProject callback to show the ProjectForm modal in App.js
        if (onNewProject) {
          onNewProject();
        }
        break;
      case 'personal':
        setShowNewPersonalTaskForm(true);
        break;
    }
  };

  const renderContent = () => {
    if (!view) {
      return (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Welcome to {organizationName}</h3>
          <p className="text-gray-600">Select a section from the sidebar to get started</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      );
    }

    if (view === 'messaging') {
      return <InternalMessaging user={user} />;
    }

    if (view === 'personal') {
      return (
        <div className="w-full flex flex-col">
          {/* Personal Tasks Header */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <UserCheck className="w-6 h-6 text-purple-600" />
                <h1 className="text-2xl font-bold text-gray-900">Personal Tasks</h1>
              </div>
              <button 
                onClick={() => setShowNewPersonalTaskForm(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
              >
                <Plus className="w-4 h-4" />
                <span>New Personal Task</span>
              </button>
            </div>
          </div>
          
          {/* Personal Tasks Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <PersonalTasksKanban
              user={user}
              onSettingsClick={() => setShowPersonalTasksSettings(true)}
            />
          </div>
        </div>
      );
    }

    // Recurring Templates View
    if (view === 'recurring') {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <RecurringTemplatesList 
            context="team"
            contextId={user?.team_id}
            contextName={organizationName}
          />
        </div>
      );
    }

    const filteredData = data.filter(item => 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subject?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="space-y-6">
        {/* Projects: Grid view - left-justified, wraps to next line */}
        {view === 'projects' && projectsTab === 'projects' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredData.map(item => (
              <div 
                key={item.id} 
                className={`bg-white rounded-xl border-2 p-8 hover:shadow-2xl hover:border-purple-300 transition-all cursor-pointer transform hover:scale-[1.01] ${
                  item.archived ? 'border-gray-300 opacity-75' : 'border-gray-200'
                }`}
                onClick={() => handleProjectClick(item)}
              >
                <div className="flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold text-gray-900 hover:text-purple-600 flex-1">{item.name}</h3>
                      <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleArchiveProject(e, item.id, item.archived)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title={item.archived ? "Unarchive project" : "Archive project"}
                        >
                          {item.archived ? (
                            <ArchiveRestore className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Archive className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                        {!item.is_general && (
                          <button
                            onClick={(e) => handleDeleteProject(e, item.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete project"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.isDefault && (
                        <span className="px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full">
                          Default
                        </span>
                      )}
                      {item.archived && (
                        <span className="px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
                          Archived
                        </span>
                      )}
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'Active' ? 'bg-green-100 text-green-800' :
                        item.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        item.status === 'Planning' ? 'bg-yellow-100 text-yellow-800' :
                        item.status === 'Review' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                  </div>
                  
                  {item.description && (
                    <p className="text-base text-gray-600 mb-6 line-clamp-3">{item.description}</p>
                  )}
                  
                  <div className="flex flex-col gap-3 text-sm text-gray-600 mt-auto">
                    {item.client_name && (
                      <span className="flex items-center">
                        <span className="font-semibold mr-2">Client:</span> {item.client_name}
                      </span>
                    )}
                    <span className="flex items-center">
                      <CheckSquare className="w-4 h-4 mr-2" />
                      <span className="font-semibold">{item.task_count || 0}</span> tasks
                    </span>
                    {item.due_date && (
                      <span className="flex items-center">
                        <span className="font-semibold mr-2">Due:</span> 
                        {new Date(item.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {item.created_at && (
                      <span className="flex items-center text-xs">
                        <span className="font-semibold mr-2">Created:</span> 
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {filteredData.length === 0 && (
              <div className="col-span-full text-center py-16 bg-white rounded-xl border-2 border-gray-200">
                <FolderOpen className="w-20 h-20 text-gray-400 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">No projects found</h3>
                <p className="text-lg text-gray-600">
                  {searchTerm ? 'Try adjusting your search terms' : 'No company projects available yet'}
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Recurring Templates Tab Content */}
        {view === 'projects' && projectsTab === 'recurring' && (
          <div className="w-full">
            <RecurringTaskTemplates 
              user={user}
              onEditTask={(task) => {
                // Navigate to task URL for consistent behavior
                console.log('🔗 Navigating to task:', task.id);
                navigate(`/tasks/${task.id}`);
              }}
            />
          </div>
        )}
        
        {/* Other views: Use grid layout */}
        {view !== 'projects' && (
          <div className={`grid gap-4 ${
            view === 'messaging' ? 'grid-cols-1' :
            'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {filteredData.map(item => (
              <div key={item.id} className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer">
                {view === 'messaging' && (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{item.subject}</h3>
                        {item.unread && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <span>From: {item.from}</span>
                        <span>{item.timestamp}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {filteredData.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">{getViewIcon()}</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
                <p className="text-gray-600">
                  {searchTerm ? 'Try adjusting your search terms' : `No ${view} available yet`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={view === 'messaging' ? 'flex-1 flex flex-col min-h-0 h-full overflow-hidden' : view === 'personal' ? 'w-full' : 'p-8 max-w-7xl mx-auto'}>
      {/* Timer Widget - Show if timer is running and not in messaging/personal view */}
      {currentTimer?.is_running && view !== 'messaging' && view !== 'personal' && (
        <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <Timer className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-green-800">
                  {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                </div>
                <div className="text-sm text-green-600">
                  {currentTimer.task_name || 'Active Timer'}
                </div>
              </div>
            </div>
            <button
              onClick={() => handlePersonalTaskTimer(currentTimer.task_id)}
              className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Square className="w-4 h-4" />
              <span>Stop</span>
            </button>
          </div>
        </div>
      )}

      {/* For messaging view, render content directly without header */}
      {view === 'messaging' ? (
        renderContent()
      ) : (
        <>
          {/* Header - Centered and Larger */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="text-purple-600">{getViewIcon()}</div>
              <h1 className="text-4xl font-bold text-gray-900">{getViewTitle()}</h1>
            </div>
            
            {/* Tabs for Projects View */}
            {view === 'projects' && (
              <div className="flex items-center justify-center mb-6">
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setProjectsTab('projects')}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      projectsTab === 'projects'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <FolderOpen className="w-4 h-4" />
                      <span>Projects</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setProjectsTab('recurring')}
                    className={`px-6 py-2 rounded-lg font-medium transition-all ${
                      projectsTab === 'recurring'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🔄</span>
                      <span>Recurring Templates</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-center gap-4">
              {view !== 'messaging' && view !== 'personal' && view !== 'recurring' && projectsTab !== 'recurring' && (
                <button 
                  onClick={handleAdd}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg text-lg font-medium"
                >
                  <Plus className="w-5 h-5" />
              <span>{getAddButtonText()}</span>
            </button>
          )}
          {view === 'projects' && projectsTab === 'projects' && (
            <label className="inline-flex items-center space-x-2 px-4 py-3 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-base font-medium text-gray-700">Show Archived</span>
            </label>
          )}
        </div>
      </div>

      {/* Search Bar - Centered and Larger */}
      {view && view !== 'messaging' && view !== 'personal' && view !== 'recurring' && projectsTab !== 'recurring' && (
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="w-full max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                className="pl-12 pr-4 py-3 w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                placeholder={`Search ${view}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button className="flex items-center space-x-2 px-5 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter className="w-5 h-5" />
            <span className="text-lg">Filter</span>
          </button>
        </div>
      )}

          {/* Content for non-messaging views */}
          <div className={view === 'personal' ? 'w-full' : ''}>
            {renderContent()}
          </div>
        </>
      )}

      {/* Enhanced Personal Task Modal */}
      <PersonalTaskModal
        isOpen={showNewPersonalTaskForm}
        onClose={() => {
          setShowNewPersonalTaskForm(false);
          setSelectedPersonalTask(null);
        }}
        onTaskCreated={handlePersonalTaskCreated}
        onTaskUpdated={handlePersonalTaskUpdated}
        user={user}
        existingTask={selectedPersonalTask}
      />

      {/* Recurring Task Modal */}
      <RecurringTaskModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSave={(rule, preview) => {
          setRecurringRule(rule);
          setRecurringPreview(preview);
        }}
        initialRule={recurringRule}
      />

      {/* Personal Tasks Settings Modal */}
      <PersonalTasksSettings
        isOpen={showPersonalTasksSettings}
        onClose={() => setShowPersonalTasksSettings(false)}
        user={user}
      />
    </div>
  );
};

export default OrganizationDashboard;
